import { Hotel, Video, Building2, Activity } from "lucide-react";

/**
 * Data for the reusable ServicePage template.
 * One object per SEO landing page. Keyed by `path`.
 */
export const LANDING_SERVICES = [
  {
    path: "/doctor-to-hotel-vietnam",
    title: "Doctor to Hotel",
    icon: Hotel,
    seoTitle: "Doctor to Hotel in Vietnam | Hotel Doctor Visit Support",
    metaDescription:
      "Doctors Vietnam helps coordinate in-person visits from a licensed local doctor to your hotel, home or serviced apartment in Vietnam, where available.",
    h1: "Doctor Visits to Your Hotel in Vietnam",
    serviceType: "Hotel doctor visit coordination",
    intro:
      "Feeling unwell while travelling or staying in Vietnam? Doctors Vietnam can help coordinate an in-person visit from a licensed local doctor to your hotel, home or serviced apartment where available.",
    included: [
      "Initial doctor consultation",
      "Basic medical assessment",
      "Treatment recommendations from the doctor",
      "Prescription coordination where appropriate",
      "Clinic or hospital referral if required",
      "English-speaking coordination",
      "Follow-up assistance",
    ],
    limitations: [
      "Visits are subject to doctor availability and location.",
      "Doctors Vietnam does not employ the treating doctor and does not provide diagnosis or treatment.",
    ],
    faqs: [
      { q: "Can a doctor really come to my hotel?", a: "Where available, yes. We coordinate a licensed local doctor to visit your hotel, home or serviced apartment for non-emergency care." },
      { q: "Can the doctor prescribe medication?", a: "Prescriptions can be coordinated where appropriate. Any medical decision is made by the treating doctor, not by Doctors Vietnam." },
      { q: "Do you offer English-speaking support?", a: "Yes. Coordination is available in English and other supported languages from your first message." },
      { q: "Is this an emergency service?", a: "No. For life-threatening emergencies call 115 in Vietnam or go to the nearest hospital emergency department. We coordinate non-emergency visits only." },
    ],
  },
  {
    path: "/online-doctor-consultation-vietnam",
    title: "Online Doctor Consultation",
    icon: Video,
    seoTitle: "Online Doctor Consultation in Vietnam | English Support",
    metaDescription:
      "Arrange an online video consultation with a suitable doctor from your hotel, home or current location in Vietnam, with English-speaking coordination.",
    h1: "Online Doctor Consultations in Vietnam",
    serviceType: "Online doctor consultation coordination",
    intro:
      "Arrange an online consultation with a suitable doctor from your hotel, home or current location in Vietnam.",
    included: [
      "Video doctor consultation",
      "Review of symptoms and concerns",
      "Guidance from the doctor",
      "Clinic referral where required",
      "Prescription guidance where legally appropriate",
      "English-speaking coordination",
      "Follow-up booking",
    ],
    limitations: [
      "An online doctor cannot diagnose every condition — some issues require an in-person visit or tests.",
      "Consultations are subject to doctor availability. Doctors Vietnam does not provide diagnosis or treatment.",
    ],
    faqs: [
      { q: "Who will I speak to?", a: "A suitable licensed doctor, connected by video where possible, with English-speaking coordination arranged for you." },
      { q: "Can an online doctor diagnose everything?", a: "No. Online consultations are helpful for advice and guidance, but not every condition can be assessed remotely — you may be referred for an in-person visit." },
      { q: "Can I get a prescription online?", a: "Prescription guidance is provided where legally appropriate, at the doctor's discretion." },
      { q: "What if I need to be seen in person?", a: "We can coordinate a hotel doctor visit or a clinic appointment as the appropriate next step." },
    ],
  },
  {
    path: "/clinic-hospital-booking-vietnam",
    title: "Clinic & Hospital Booking",
    icon: Building2,
    seoTitle: "Clinic and Hospital Appointments in Vietnam | Medical Coordination",
    metaDescription:
      "Doctors Vietnam helps international patients, travellers and residents identify suitable medical providers and coordinate clinic and hospital appointments in Vietnam.",
    h1: "Clinic and Hospital Appointment Support in Vietnam",
    serviceType: "Clinic and hospital appointment coordination",
    intro:
      "We help international patients, travellers and residents identify suitable medical providers and coordinate appointments.",
    included: [
      "Same-day appointment requests where available",
      "General doctor appointments",
      "Diagnostic tests",
      "Specialist consultations",
      "Hospital registration assistance",
      "Translation coordination",
      "Medical report preparation",
      "Follow-up booking",
    ],
    limitations: [
      "We cannot guarantee same-day availability.",
      "Appointments are subject to provider confirmation. Doctors Vietnam does not diagnose or provide treatment.",
    ],
    faqs: [
      { q: "Can you get me a same-day appointment?", a: "We make same-day requests where availability allows, but we cannot guarantee same-day appointments." },
      { q: "Do you help with specialists?", a: "Yes. We help identify suitable providers and coordinate specialist consultations and diagnostic tests." },
      { q: "Can you help with translation?", a: "Yes. Translation coordination is available to help you communicate with providers." },
      { q: "Do you provide the medical treatment?", a: "No. Care is delivered by the independent clinic or hospital. Doctors Vietnam coordinates the appointment." },
    ],
  },
  {
    path: "/urgent-medical-support-vietnam",
    title: "Urgent Medical Support",
    icon: Activity,
    seoTitle: "Urgent Medical Support in Vietnam | Non-Emergency Coordination",
    metaDescription:
      "Non-emergency urgent medical coordination in Vietnam. Doctors Vietnam helps you find the most appropriate next step quickly — hotel doctor, online consultation, clinic or hospital.",
    h1: "Urgent Non-Emergency Medical Support in Vietnam",
    serviceType: "Non-emergency urgent medical coordination",
    emergencyProminent: true,
    intro:
      "When you need medical help quickly but the situation is not life-threatening, Doctors Vietnam can help coordinate the most appropriate next step.",
    included: [
      "Hotel doctor coordination",
      "Online doctor consultation",
      "Urgent clinic appointment request",
      "Hospital guidance",
      "Translation assistance",
      "Transport coordination",
      "Follow-up support",
    ],
    limitations: [
      "Doctors Vietnam coordinates non-emergency medical support only.",
      "It is not an emergency service, ambulance service or hospital, and does not diagnose or provide treatment.",
      "Availability is confirmed individually.",
    ],
    faqs: [
      { q: "Is Doctors Vietnam an emergency service?", a: "No. Doctors Vietnam is not an emergency or ambulance service. For life-threatening emergencies call 115 in Vietnam or go directly to the nearest hospital emergency department." },
      { q: "What counts as non-emergency?", a: "Situations needing prompt attention that are not life-threatening — for example fever, minor injuries, or travel-related illness." },
      { q: "How quickly can you help?", a: "We aim to respond as soon as possible via WhatsApp. Timing depends on your location and provider availability." },
      { q: "What can you coordinate?", a: "A hotel doctor visit, an online consultation, an urgent clinic appointment request, hospital guidance, translation and transport coordination." },
    ],
  },
];

export const getLandingService = (path) => LANDING_SERVICES.find((s) => s.path === path);
